// =============================================================================
// clientes/src/paginas/Catalogo.tsx — o catálogo que o parceiro navega.
//
// O que aparece vem TODO de catalogo_b2b: preço já com o desconto dele
// (melhor-dos-dois com a promoção), disponibilidade como um badge binário.
// NUNCA quantidade, local, custo ou fornecedor — regra de contrato da tela.
// =============================================================================
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CatalogoB2BRow } from '@core/types';
import Aviso from '../componentes/Aviso';
import Carregando from '../componentes/Carregando';
import {
  adicionar, lerCarrinho, salvarCarrinho, type ItemCarrinho,
} from '../lib/carrinho';
import { catalogo, categorias } from '../lib/consultas';
import { brl } from '../lib/formato';
import { mensagemDeErro, urlDaFoto } from '../lib/supabase';

const POR_PAGINA = 24;

export default function Catalogo() {
  const [busca, setBusca] = useState('');
  const [aplicada, setAplicada] = useState('');
  const [categoria, setCategoria] = useState<string | null>(null);
  const [pagina, setPagina] = useState(0);
  const [adicionado, setAdicionado] = useState<string | null>(null);

  // Debounce simples: a busca "aplicada" corre 300 ms atrás da digitada.
  useEffect(() => {
    const t = setTimeout(() => { setAplicada(busca); setPagina(0); }, 300);
    return () => clearTimeout(t);
  }, [busca]);

  const cats = useQuery({ queryKey: ['categorias'], queryFn: categorias, staleTime: 10 * 60_000 });
  const itens = useQuery({
    queryKey: ['catalogo', aplicada, categoria, pagina],
    queryFn: () => catalogo(aplicada, categoria, POR_PAGINA, pagina * POR_PAGINA),
  });

  const total = itens.data?.[0]?.total_resultados ?? 0;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  const porNoCarrinho = (p: CatalogoB2BRow) => {
    const item: ItemCarrinho = {
      product_id: p.product_id, sku: p.sku, descricao: p.descricao,
      preco: Number(p.preco), pronta_entrega: p.pronta_entrega, quantidade: 1,
    };
    salvarCarrinho(adicionar(lerCarrinho(), item));
    setAdicionado(p.product_id);
    setTimeout(() => setAdicionado(null), 1500);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <input className="campo" placeholder="Busque por nome ou código…"
               value={busca} onChange={(e) => setBusca(e.target.value)} />
        <select className="campo" value={categoria ?? ''}
                onChange={(e) => { setCategoria(e.target.value || null); setPagina(0); }}>
          <option value="">Todas as categorias</option>
          {(cats.data ?? []).map((c) => (
            <option key={c.categoria} value={c.categoria}>
              {c.categoria} ({c.itens})
            </option>
          ))}
        </select>
      </div>

      {itens.error ? <Aviso tipo="erro">{mensagemDeErro(itens.error)}</Aviso> : null}
      {itens.isPending && <Carregando texto="Buscando peças" />}

      <ul className="space-y-2">
        {(itens.data ?? []).map((p) => (
          <li key={p.product_id} className="cartao flex gap-3 p-3">
            {p.tem_foto ? (
              <img src={urlDaFoto(p.product_id)} alt="" loading="lazy"
                   className="h-16 w-16 shrink-0 border border-edge object-cover" />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center border border-dashed border-edge text-[0.625rem] text-dim">
                sem foto
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[0.625rem] uppercase text-dim">
                {p.sku}{p.marca ? ` · ${p.marca}` : ''}
              </p>
              <p className="text-sm font-bold leading-snug">{p.descricao}</p>
              {p.aplicacoes && (
                <p className="truncate text-xs text-mute">{p.aplicacoes}</p>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="tabular-nums text-base font-black">
                  {brl(Number(p.preco))}
                </span>
                {Number(p.preco_cheio) > Number(p.preco) && (
                  <span className="text-xs text-dim line-through">{brl(Number(p.preco_cheio))}</span>
                )}
                {p.pronta_entrega ? (
                  <span className="text-xs font-semibold text-ok">pronta entrega</span>
                ) : (
                  <span className="text-xs font-semibold text-alerta">sob encomenda</span>
                )}
              </div>
            </div>
            <button type="button"
                    className="botao-primario self-center px-3 py-2 text-xs"
                    onClick={() => porNoCarrinho(p)}>
              {adicionado === p.product_id ? '✓' : '+ Carrinho'}
            </button>
          </li>
        ))}
        {(itens.data ?? []).length === 0 && !itens.isPending && (
          <li className="text-sm text-dim">
            Nada com esse nome. Não achou a peça? Ligue para a loja — o balcão
            acha pelo carro.
          </li>
        )}
      </ul>

      {paginas > 1 && (
        <div className="flex items-center justify-between">
          <button type="button" className="botao-secundario px-3 py-1.5 text-xs"
                  disabled={pagina === 0} onClick={() => setPagina((n) => n - 1)}>
            ← Anterior
          </button>
          <span className="text-xs text-dim">página {pagina + 1} de {paginas}</span>
          <button type="button" className="botao-secundario px-3 py-1.5 text-xs"
                  disabled={pagina + 1 >= paginas} onClick={() => setPagina((n) => n + 1)}>
            Próxima →
          </button>
        </div>
      )}
    </div>
  );
}
